import { useState } from "react";
import axios from "axios";
import { Sparkles, Globe, ExternalLink, ChevronDown, FileText, Tag, Users, HelpCircle, Quote, Image as ImageIcon, Clock, CheckCircle, Share2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Compact 1-Zeilen-Status-Strip für den Builder-Header. Vereint Industry-
 * Profile + Scraped-Summary + Requested-Subpages-Queue in einer schmalen,
 * aufklappbaren Leiste. Spart 100-150px vertikalen Platz für das Editing-
 * und Vorschau-Fenster.
 */
export const CompactProjectStatusStrip = ({
  industryProfile,
  setIndustryProfile,
  scrapedSummary,
  requestedSubpages = [],
  projectId,
  API,
  setProject,
  canApply = true,
}) => {
  const [open, setOpen] = useState(false);

  const ip = industryProfile;
  const ss = scrapedSummary || {};
  const hasIndustry = !!ip;
  const hasScraped = !!ss.available;
  const hasQueue = requestedSubpages.length > 0;

  // Build chip array for compact line
  const chips = [];
  if (hasIndustry) {
    chips.push(
      <span key="ip" className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[11px] font-semibold ${ip.is_applied ? "bg-primary/10 text-primary" : "bg-amber-500/10 text-amber-700 dark:text-amber-400"}`}>
        <Sparkles size={10} />
        {ip.label}
        {ip.is_applied && <CheckCircle size={10} className="ml-0.5" />}
      </span>
    );
  }
  if (hasIndustry && ip.features?.length) {
    chips.push(
      <span key="features" className="text-[11px] text-muted-foreground">
        {ip.features.length} Features
      </span>
    );
  }
  if (hasScraped) {
    const parts = [];
    if (ss.subpages_count > 0) parts.push(`${ss.subpages_count} Subseiten`);
    if (ss.services_count > 0) parts.push(`${ss.services_count} Leistungen`);
    if (ss.team_count > 0) parts.push(`${ss.team_count} Team`);
    if (ss.gallery_count > 0) parts.push(`${ss.gallery_count} Bilder`);
    if (ss.has_logo) parts.push("Logo");
    chips.push(
      <span key="scraped" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[11px] bg-emerald-500/10 text-emerald-700 dark:text-emerald-400">
        <Globe size={10} />
        Crawl: {parts.slice(0, 3).join(" · ")}
        {parts.length > 3 && <span className="opacity-70">+{parts.length - 3}</span>}
      </span>
    );
  }
  if (hasQueue) {
    chips.push(
      <span key="queue" className="text-[11px] px-2 py-0.5 rounded-sm bg-muted text-muted-foreground">
        {requestedSubpages.length} in Queue
      </span>
    );
  }

  const applyProfile = async () => {
    if (!ip) return;
    if (!window.confirm(`Branchen-Profil „${ip.label}" anwenden?\n\nDas aktiviert: ${ip.features.join(", ")}\nund legt ${ip.suggested_pages.length} passende Unterseiten in die Warteschlange.\n\nBestehende Buchungs-/Anfrage-Einstellungen werden ÜBERSCHRIEBEN.`)) return;
    try {
      const res = await axios.post(`${API}/projects/${projectId}/apply-industry-profile`, {}, { withCredentials: true });
      toast.success(`Profil „${res.data.profile_label}" angewendet — ${res.data.subpages_queued} Unterseiten in Queue.`);
      setIndustryProfile({ ...ip, is_applied: true });
      try {
        const p = await axios.get(`${API}/projects/${projectId}`, { withCredentials: true });
        setProject(p.data);
      } catch (_) {}
    } catch (e) {
      toast.error(e.response?.data?.detail || "Profil anwenden fehlgeschlagen");
    }
  };

  return (
    <div className="rounded-sm border border-border bg-card" data-testid="compact-status-strip">
      {/* Compact 1-line strip */}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-muted/30 transition-colors"
        data-testid="compact-status-toggle"
      >
        <div className="flex-1 min-w-0 flex items-center gap-2 flex-wrap">
          {chips}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {hasIndustry && !ip.is_applied && canApply && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); applyProfile(); }}
              className="px-2 py-0.5 rounded-sm bg-foreground text-background text-[10px] font-semibold hover:opacity-90"
              data-testid="industry-profile-apply-btn-compact"
            >
              Anwenden
            </button>
          )}
          <ChevronDown
            size={14}
            className={`text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`}
          />
        </div>
      </button>

      {/* Expanded details */}
      {open && (
        <div className="border-t border-border px-3 py-2.5 space-y-2 bg-muted/10" data-testid="compact-status-expanded">
          {hasIndustry && (
            <div className="text-xs">
              <div className="font-semibold text-foreground">
                Branchen-Profil „{ip.label}"
                {ip.is_applied && <span className="ml-2 text-[10px] text-primary uppercase tracking-wider">aktiv</span>}
              </div>
              <div className="text-muted-foreground mt-0.5">
                Features: <span className="text-foreground">{ip.features.join(" · ")}</span>
                {ip.suggested_pages?.length > 0 && (
                  <> · {ip.suggested_pages.length} vorgeschlagene Unterseiten</>
                )}
              </div>
            </div>
          )}
          {hasScraped && (
            <div className="text-xs pt-1.5 border-t border-border/40">
              <div className="font-semibold text-foreground inline-flex items-center gap-2">
                Aus alter Website übernommen
                {ss.source_url && (
                  <a
                    href={ss.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[11px] font-normal text-emerald-700 dark:text-emerald-400 hover:underline inline-flex items-center gap-1"
                    onClick={(e) => e.stopPropagation()}
                    data-testid="scraped-source-link"
                  >
                    {(() => { try { return new URL(ss.source_url).hostname.replace(/^www\./, ""); } catch (_) { return ""; } })()}
                    <ExternalLink size={10} />
                  </a>
                )}
              </div>
              <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-[11px] text-muted-foreground">
                {ss.subpages_count > 0 && <span className="inline-flex items-center gap-1"><FileText size={10} />{ss.subpages_count} Subseiten</span>}
                {ss.services_count > 0 && <span className="inline-flex items-center gap-1"><Sparkles size={10} />{ss.services_count} Leistungen</span>}
                {ss.prices_count > 0 && <span className="inline-flex items-center gap-1"><Tag size={10} />{ss.prices_count} Preise</span>}
                {ss.team_count > 0 && <span className="inline-flex items-center gap-1"><Users size={10} />{ss.team_count} Team</span>}
                {ss.faqs_count > 0 && <span className="inline-flex items-center gap-1"><HelpCircle size={10} />{ss.faqs_count} FAQs</span>}
                {ss.testimonials_count > 0 && <span className="inline-flex items-center gap-1"><Quote size={10} />{ss.testimonials_count} Testimonials</span>}
                {ss.gallery_count > 0 && <span className="inline-flex items-center gap-1"><ImageIcon size={10} />{ss.gallery_count} Bilder</span>}
                {ss.has_hours && <span className="inline-flex items-center gap-1"><Clock size={10} />Öffnungszeiten</span>}
                {ss.has_logo && <span className="inline-flex items-center gap-1"><CheckCircle size={10} className="text-emerald-600" />Logo</span>}
                {ss.social_count > 0 && <span className="inline-flex items-center gap-1"><Share2 size={10} />{ss.social_count} Social</span>}
              </div>
            </div>
          )}
          {hasQueue && (
            <div className="text-xs pt-1.5 border-t border-border/40">
              <div className="font-semibold text-foreground mb-1">Subpages-Warteschlange</div>
              <div className="flex flex-wrap gap-1">
                {requestedSubpages.slice(0, 12).map((sp, idx) => (
                  <span
                    key={idx}
                    className="text-[10px] px-1.5 py-0.5 rounded-sm bg-card border border-border text-foreground"
                    data-testid={`queued-subpage-${idx}`}
                    title={sp.brief || ""}
                  >
                    {sp.title}
                  </span>
                ))}
                {requestedSubpages.length > 12 && (
                  <span className="text-[10px] text-muted-foreground">+{requestedSubpages.length - 12} weitere</span>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
